import { Button, Icon, MenuItem } from "@mui/material";
import _ from "lodash";
import CalendarRange from "mdi-material-ui/CalendarRange";
import SortAlphabeticalVariant from "mdi-material-ui/SortAlphabeticalVariant";
import Update from "mdi-material-ui/Update";
import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { v4 as uuid } from "uuid";
import { useTranslation } from "react-i18next";

import { ASSET_FILETYPE, ASSET_RESOLUTION, AssetType } from "common/constants";
import { ActionPermissionsObject, IWorkspaceResourceAsset, WorkspaceResourceType, PermissionActionType, WorkspacePlanTier } from "common/interfaces";
import Banner from "js/Components/Banner";
import { Breadcrumb } from "js/Components/Breadcrumb";
import WorkspaceController, { WorkspaceControllerState } from "js/controllers/WorkspaceController";
import getLogger, { LogGroup } from "js/core/logger";
import { ds } from "js/core/models/dataService";
import { sortTagsByPopularity } from "js/core/utilities/utilities";
import withProgressDialog from "js/core/utilities/withProgressDialog";
import { app } from "js/namespaces";
import { ShowConfirmationDialog, ShowDialog, ShowDialogAsync, ShowErrorDialog } from "js/react/components/Dialogs/BaseDialog";
import FetchingClickShield from "js/react/components/FetchingClickShield";
import { Gap20, Gap30 } from "js/react/components/Gap";
import { NoMatchNotice, NoTeamAssetNotice } from "js/react/components/Notice";
import { FlexBox } from "js/react/components/LayoutGrid";
import { IconMenu, RoundIconButton, UIPane } from "js/react/components/UiComponents";
import AddAssetsContainer from "js/react/views/AddAssets/AddAssetsContainer";
import { FilterDropDown } from "js/react/views/AddSlide/Panes/Components/FilterDropDown";
import {
    SearchBarContainer,
    SearchBarInnerContainer,
    SlideSearchInput,
    UIPaneResultsContainer,
} from "js/react/views/AddSlide/Panes/Components/SearchBox";
import { SortMenu } from "js/react/views/AddSlide/Panes/Components/SortMenu";
import ProgressDialog from "js/react/components/Dialogs/ProgressDialog";
import ChangePlanDialog from "js/react/components/Dialogs/ChangePlanDialog";
import { MultiSelectThumbnailGrid, Thumbnail, ThumbnailContainer } from "../../AddSlide/Panes/Components/ThumbnailGrid";
import { BulkAssetUploadDialog, BulkSetTagsDialog } from "../dialogs/BulkAssetUploadDialog";
import CreateTeamAssetDialog from "../dialogs/CreateTeamAssetDialog";
import TeamAssetSettingsDialog from "../dialogs/TeamAssetSettingsDialog";

const logger = getLogger(LogGroup.TEAMS);

const StyledUIPaneResultsContainer = styled(UIPaneResultsContainer) <{ editable?: boolean }>`
    ${props => props.editable ? "" : "height: 50px"}
`;

const StyledSearchBarContainer = styled(SearchBarContainer) <{ editable?: boolean }>`
    background: #eee;
    ${props => props.editable ? "" : "height: 50px"}
`;

const StyledSearchBarInnerContainer = styled(SearchBarInnerContainer) <{ editable?: boolean }>`
    background: ${props => props.editable ? "white" : "#eee"};
`;

const ThumbnailGridContainer = styled.div`
    padding: 20px;
    margin: 0px 0px 20px;
    overflow: hidden;

    .dark-mode & {
        background: #333;
    }
`;

const UploadFileInput = styled.input`
    width: 100%;
    height: 100%;
    opacity: 0;
    position: absolute;

`;

const MEDIA_FILTER_OPTIONS = [
    {
        id: "all",
        nameKey: "All Media"
    },
    {
        id: "image",
        nameKey: "Photos"
    },
    {
        id: "logo",
        nameKey: "Logos"
    },
];

const SORT_OPTIONS = [
    {
        value: "name",
        labelKey: "Name",
        icon: <SortAlphabeticalVariant />
    },
    {
        value: "createdAt",
        labelKey: "Created At",
        reverse: true,
        icon: <CalendarRange />
    },
    {
        value: "modifiedAt",
        labelKey: "Modified At",
        reverse: true,
        icon: <Update />
    },
];

interface TeamAssetsPaneProps {
    prefilledSearchQuery?: string;
    editable?: boolean;
    addAssetCallback?: (asset: any) => void;
    handleConfirm?: () => void;
}

const TeamAssetsPane = WorkspaceController.withInitializedState(
    function TeamAssetsPane(props: TeamAssetsPaneProps & WorkspaceControllerState) {
        const { prefilledSearchQuery, editable, addAssetCallback, handleConfirm, resources, workspace } = props;
        const { t } = useTranslation();

        const [isLoading, setIsLoading] = useState(true);
        const [selectedMediaFilter, setSelectedMediaFilter] = useState<string>("all");
        const [selectedTagFilter, setSelectedTagFilter] = useState<string>("  all  ");
        const [sortBy, setSortBy] = useState<string>("createdAt");
        const [sortDir, setSortDir] = useState(1);
        const [searchTerm, setSearchTerm] = useState<string>(prefilledSearchQuery?.trim().toLowerCase() || "");
        const mediaFilterOptions = MEDIA_FILTER_OPTIONS.map(option => ({ ...option, name: t(option.nameKey) }));
        const sortOptions = SORT_OPTIONS.map(option => ({ ...option, label: t(option.labelKey) }));

        const canManageSharedResources = WorkspaceController.actionPermissions[ActionPermissionsObject.SHARED_RESOURCES].manage;
        const canUpgradeToManageSharedResources = WorkspaceController.canUpgradeToRemoveActionRestriction(ActionPermissionsObject.SHARED_RESOURCES, PermissionActionType.MANAGE);
        const canUseSharedResources = WorkspaceController.actionPermissions[ActionPermissionsObject.SHARED_RESOURCES].use;

        const assets = resources.filter(resource =>
            resource.resourceType === WorkspaceResourceType.ASSET &&
            (resource.metadata.type === "image" || resource.metadata.type === "logo")
        ) as IWorkspaceResourceAsset[];

        const existingTags = () => {
            let sortedTags = sortTagsByPopularity(assets.filter((asset: IWorkspaceResourceAsset) => editable || asset.metadata.isPublished).map((asset: IWorkspaceResourceAsset) => asset.tags));
            resources.forEach(resource => {
                sortedTags.push(...(resource.tags || []));
            });

            return _.uniqBy(sortedTags.flat().map(_.trim), _.toLower);
        };

        useEffect(() => {
            setIsLoading(false);
        }, []);

        const handleSearch = async (searchTerm: string) => {
            setSearchTerm(searchTerm?.trim().toLowerCase());
        };

        const createOrUpdateTeamAsset = async (
            fromAssetId: string,
            attributesOverrides: { tags?: string[], name?: string, type?: string } = {},
            assetResource: IWorkspaceResourceAsset = null,
            isNewAsset: boolean = false,
        ) => {
            if (assetResource) {
                try {
                    await WorkspaceController.updateWorkspaceResource({
                        resourceId: assetResource.resourceId,
                        resourceType: WorkspaceResourceType.ASSET,
                        tags: attributesOverrides?.tags ?? assetResource?.tags ?? [],
                        metadata: {
                            type: attributesOverrides?.type ?? assetResource.metadata.type,
                            createdBy: assetResource.metadata?.createdBy,
                            name: attributesOverrides?.name ?? assetResource.metadata.name,
                            alt: assetResource.metadata.alt,
                            isPublished: assetResource.metadata?.isPublished,
                            actualId: fromAssetId
                        }
                    }, { isNewAsset, isReplacedAsset: !!assetResource });

                    const asset = await ds.assets.getAssetById(assetResource.resourceId);
                    await asset.reload();
                } catch (error) {
                    logger.error(error, "[TeamAssetsPane] Error updating workspace asset");
                }

                return;
            }

            try {
                const asset = await ds.assets.cloneAsset(fromAssetId, {
                    userId: null,
                    id: `${workspace.id}:${uuid()}`
                }, false);

                if (!asset || !asset.id) {
                    throw new Error("Failed to clone asset: asset is missing or invalid.");
                }

                await WorkspaceController.createWorkspaceResource({
                    resource: {
                        resourceId: asset.id,
                        resourceType: WorkspaceResourceType.ASSET,
                        folderIds: [],
                        tags: attributesOverrides?.tags || [],
                        metadata: {
                            type: attributesOverrides?.type ?? asset.get("type"),
                            createdBy: app.user.id,
                            modifiedBy: app.user.id,
                            name: attributesOverrides?.name ?? asset.get("assetName"),
                            alt: asset.get("name") ?? "",
                            isPublished: true,
                            actualId: fromAssetId
                        }
                    },
                    defaultPermissions: "shared"
                }, { isNewAsset, assetType: attributesOverrides?.type ?? asset.get("type") }) as IWorkspaceResourceAsset;

                await asset.reload();
            } catch (error) {
                logger.error(error, "[TeamAssetsPane] Error cloning and creating workspace asset");
            }
        };

        const handleAddItem = async () => {
            const { createdAssetModel, isNewAsset } = await new Promise<{ createdAssetModel: any, isNewAsset: boolean }>(resolve => {
                ShowDialogAsync(AddAssetsContainer, {
                    assetType: AssetType.IMAGE,
                    hideTeamAssets: true,
                    hideIcons: true,
                    hideVideos: true,
                    callback: (model, isNew) => {
                        resolve({ createdAssetModel: model, isNewAsset: isNew });
                    }
                });
            });

            if (!createdAssetModel) {
                return;
            }

            ShowDialog(CreateTeamAssetDialog, {
                type: createdAssetModel.content_type,
                name: createdAssetModel.assetName,
                tags: createdAssetModel.tags,
                existingTags: existingTags(),
                callback: async ({ type, name, tags }) => {
                    try {
                        await createOrUpdateTeamAsset(createdAssetModel.content_value, { type, name, tags }, null, isNewAsset);
                    } catch (err) {
                        logger.error(err, "[TeamAssetsPane] Error creating team asset", { name });
                        ShowErrorDialog({
                            error: t("An error occurred while creating the team asset."),
                            message: (
                                <>
                                    <p>
                                        <strong>{t("Error")}:</strong> {err.message}
                                    </p>
                                    <p>{t("We apologize for the inconvenience. Please contact us at support@beautiful.ai.")}</p>
                                </>
                            )
                        });
                    }
                }
            });
        };

        const handleUpdateAsset = async (teamAsset: IWorkspaceResourceAsset) => {
            return new Promise(resolve => {
                const showAssetsDialog = () => {
                    ShowDialog(AddAssetsContainer, {
                        assetType: teamAsset.metadata.type,
                        hideTeamAssets: true,
                        hideIcons: true,
                        hideVideos: true,
                        hidePhotos: teamAsset.metadata.type !== AssetType.IMAGE,
                        hideLogos: teamAsset.metadata.type !== AssetType.LOGO,
                        onCancel: () => resolve(false),
                        callback: async (model, isNew) => {
                            try {
                                await createOrUpdateTeamAsset(model.content_value, { type: model.content_type }, teamAsset, isNew);

                                resolve(true);
                            } catch (err) {
                                logger.error(err, "[TeamAssetsPane] Error replacing asset", { id: teamAsset.resourceId });
                                ShowErrorDialog({
                                    error: t("An error occurred while updating the asset."),
                                    message: (
                                        <>
                                            <p>
                                                <strong>{t("Error")}:</strong> {err.message}
                                            </p>
                                            <p>{t("We apologize for the inconvenience. Please contact us at support@beautiful.ai.")}</p>
                                        </>
                                    )
                                });
                                resolve(false);
                            }
                        }
                    });
                };

                ShowConfirmationDialog({
                    title: t("Update Asset"),
                    message: (
                        <>
                            <p>{t("You can update this asset with a new version and any slides that currently use this asset will be automatically updated.")}</p>
                            <p>{t("This can be useful if you need to update a team logo or headshot or other asset that you'd like to easily replace with a new version across all existing slides that use it.")}</p>
                        </>
                    ),
                    acceptCallback: showAssetsDialog,
                    cancelCallback: () => resolve(false),
                });
            });
        };

        const handleDeleteAssets = async (selection: any[]) => {
            if (await ShowConfirmationDialog({
                title: selection.length === 1
                    ? t("Are you sure you want to delete this asset?")
                    : t("Are you sure you want to delete {{count}} selected assets?", { count: selection.length }),
                message: t("Slides that already use this asset will not be changed. This action can not be undone."),
                buttonOptions: { cancelButtonVariant: "outlined", acceptButtonColor: "red" },
                okButtonLabel: t("Delete"),
            })) {
                for (let asset of selection) {
                    try {
                        await withProgressDialog({
                            title: t("Deleting Team Asset..."),
                            action: () => WorkspaceController.deleteWorkspaceResource({ resourceId: asset.resourceId, resourceType: WorkspaceResourceType.ASSET })
                        });
                    } catch (err) {
                        logger.error(err, "[TeamAssetsPane]  failed", { resourceId: asset.resourceId, resourceType: WorkspaceResourceType.ASSET });

                        ShowErrorDialog({
                            error: t("An error occurred while deleting the Team Asset."),
                            message: (
                                <>
                                    <p>
                                        <strong>{t("Error")}:</strong> {err.message}
                                    </p>
                                    <p>{t("We apologize for the inconvenience. Please contact us at support@beautiful.ai.")}</p>
                                </>
                            )
                        });
                    }
                }
            }
        };

        const handleEditSettings = async (assetResource: IWorkspaceResourceAsset) => {
            ShowDialog(TeamAssetSettingsDialog, {
                assetResource,
                existingTags: existingTags()
            });
        };

        const handleMediaFilterChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
            const selectedMediaFilter = event.target.value;
            setSelectedMediaFilter(selectedMediaFilter);
        };

        const handleTagFilterChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
            const selectedTagFilter = event.target.value;
            setSelectedTagFilter(selectedTagFilter);
        };

        const handleSort = ({ field, reverse }: { field: string, reverse: boolean }) => {
            setSortBy(field);
            setSortDir(reverse ? 1 : -1);
        };

        const getSortedFilteredAssets = () => {
            // filtering
            const assetEntries = assets.filter((asset: any) => {
                if (
                    !editable &&
                    !asset.metadata.isPublished
                ) {
                    return false;
                }

                const tags = asset.tags ?? [];

                const searchMatch = (
                    !searchTerm?.length ||
                    asset.metadata.name?.toLowerCase().includes(searchTerm) ||
                    tags.some((tag: string) => tag.toLowerCase().includes(searchTerm))
                );
                const mediaMatch = (
                    selectedMediaFilter === "all" ||
                    asset.metadata.type === selectedMediaFilter
                );
                const tagMatch = (
                    selectedTagFilter === "  all  " ||
                    tags.includes(selectedTagFilter)
                );

                return searchMatch && mediaMatch && tagMatch;
            });

            assetEntries.sort((a: any, b: any) => {
                const valueA = sortBy === "name" ? a.metadata.name : a[sortBy];
                const valueB = sortBy === "name" ? b.metadata.name : b[sortBy];
                return valueA < valueB ? sortDir : -sortDir;
            });

            return assetEntries;
        };

        const handleSetTags = (selection: any[]) => {
            ShowDialog(BulkSetTagsDialog, {
                selection,
                existingTags: existingTags(),
                onAccept: async (tags: string[]) => {
                    for (let asset of selection) {
                        try {
                            await WorkspaceController.updateWorkspaceResource({
                                ...asset,
                                tags: [...tags]
                            });
                        } catch (err) {
                            logger.error(err, "[TeamAssetsPane] handleSetTags() failed", { id: asset.id });
                        }
                    }
                }
            });
        };

        const handleBulkUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
            const files = event.target.files;

            if (!files || files.length === 0) return;

            ShowDialog(BulkAssetUploadDialog, {
                files,
                existingTags: existingTags(),
                types: AssetType.IMAGE,
                onAccept: async ({ tags, types }: { tags: string[], types: any }) => {
                    const progressDialog = ShowDialog(ProgressDialog, {
                        title: t("Uploading Assets..."),
                        progress: 0
                    });

                    for (let i = 0; i < files.length; i++) {
                        let file = files[i];
                        progressDialog.setProgress(i / files.length * 100);
                        progressDialog.setMessage(t("Uploading {{fileName}}", { fileName: file.name }));

                        let fileType;
                        if (file.type === "image/jpeg") {
                            fileType = ASSET_FILETYPE.JPG;
                        } else if (file.type === "image/png") {
                            fileType = ASSET_FILETYPE.PNG;
                        } else if (file.type === "image/gif") {
                            fileType = ASSET_FILETYPE.GIF;
                        } else if (file.type === "image/svg+xml") {
                            fileType = ASSET_FILETYPE.SVG;
                        }
                        let currentType = AssetType.IMAGE;
                        if (types === "logo") {
                            currentType = AssetType.LOGO;
                        }
                        try {
                            const asset = await ds.assets.getOrCreateImage({
                                file,
                                name: file.name,
                                fileType,
                                assetType: currentType,
                                metadata: {
                                    source: "bulk-upload"
                                }
                            });

                            await createOrUpdateTeamAsset(asset.id, { tags, name: file.name, type: currentType }, null);
                        } catch (err) {
                            logger.error(err, "[TeamAssetsPane] Bulk upload failed for file", { name: file.name });
                        }
                    }

                    progressDialog.props.closeDialog();
                }
            });
        };

        const renderContextMenu = (selection: IWorkspaceResourceAsset[]) => {
            let menuItems = [];
            if (selection.length === 1) {
                menuItems.push(<MenuItem divider key="edit" onClick={() => handleEditSettings(selection[0])}>
                    <Icon>edit</Icon>{t("Edit Asset Properties...")}
                </MenuItem>);

                menuItems.push(<MenuItem divider key="replace" onClick={() => handleUpdateAsset(selection[0])}>
                    <Icon>sync</Icon>{t("Update Asset...")}
                </MenuItem>);

                menuItems.push(<MenuItem key="trash" onClick={() => handleDeleteAssets(selection)}>
                    <Icon>delete</Icon>{t("Delete Asset")}
                </MenuItem>);
            } else {
                menuItems.push(<MenuItem divider key="tags" onClick={() => handleSetTags(selection)}>
                    <Icon>label</Icon>{t("Set Tags...")}
                </MenuItem>);
                menuItems.push(<MenuItem key="trash" onClick={() => handleDeleteAssets(selection)}>
                    <Icon>delete</Icon>{t("Delete {{count}} Selected Assets", { count: selection.length })}
                </MenuItem>);
            }

            return menuItems;
        };

        const countTags = () => {
            const assetEntries = assets.filter((asset: any) => (editable || asset.metadata.isPublished));

            const tagsMap: { [key: string]: number } = {};

            assetEntries.map((asset: any) => asset.tags).forEach((tags: string[]) => {
                tags?.forEach(tag => {
                    if (!tagsMap[tag]) {
                        tagsMap[tag] = 1;
                    } else {
                        tagsMap[tag]++;
                    }
                });
            });

            return existingTags()
                .filter(tag => tagsMap[tag])
                .map(tag => {
                    return {
                        id: tag,
                        name: tag,
                        count: tagsMap[tag]
                    };
                });
        };

        const currentCountTags = countTags();

        const tagOptions = [
            {
                id: "  all  ", // Spaces needed to prevent conflict with other tags
                name: t("All Tags"),
            },
            ...currentCountTags.map(({ id, name, count }) => {
                return {
                    id,
                    name: `${name} (${count})`,
                };
            }),
        ];

        const filteredAssets = getSortedFilteredAssets();

        return (
            <UIPane>
                <StyledSearchBarContainer
                    className="search-container"
                    editable={editable}
                >
                    {editable && canManageSharedResources && <>
                        <Breadcrumb>{t("Team Assets")}</Breadcrumb>
                        <RoundIconButton
                            onClick={() => handleAddItem()}
                        >
                            <Icon>add</Icon>
                        </RoundIconButton>
                        <Gap20 />
                        <Button size="small" color="primary">
                            <Icon>drive_folder_upload</Icon>
                            {t("Bulk Upload...")}
                            <UploadFileInput
                                type="file"
                                multiple
                                accept={"image/png,image/jpeg,image/gif,image/svg+xml"}
                                onChange={handleBulkUpload}
                            />
                        </Button>

                        <Gap30 />
                    </>}
                    {canUseSharedResources && (
                        <StyledSearchBarInnerContainer editable={editable}>
                            <SlideSearchInput
                                query={searchTerm}
                                onSearch={handleSearch}
                                placeholder={t("Search team assets...")}
                            />

                            <FilterDropDown
                                onChange={handleMediaFilterChange}
                                selectedFilter={selectedMediaFilter}
                                filterOptions={mediaFilterOptions}
                            />
                            <FilterDropDown
                                onChange={handleTagFilterChange}
                                selectedFilter={selectedTagFilter}
                                filterOptions={tagOptions}
                            />
                            <SortMenu
                                selectedOption={sortBy}
                                disabled={searchTerm.length > 0}
                                sortOptions={sortOptions}
                                onSetSort={handleSort}
                            />
                        </StyledSearchBarInnerContainer>
                    )}
                    {canUpgradeToManageSharedResources && (
                        <FlexBox center fillWidth padding="40px 0px 0px 0px">
                            <Banner
                                headerText={t("Unlock Team Assets")}
                                bodyText={t("Upgrade to a Team plan to centralize approved brand assets.")}
                                buttonLabel={t("Upgrade")}
                                onButtonClick={() => ShowDialog(ChangePlanDialog,
                                    {
                                        featuredPlan: WorkspacePlanTier.TEAM,
                                        analytics: { cta: "unlock_team_assets" }
                                    })}
                            />
                        </FlexBox>
                    )}
                </StyledSearchBarContainer>
                <StyledUIPaneResultsContainer editable={editable}>
                    <FetchingClickShield visible={isLoading} backgroundColor="#eee" />
                    {filteredAssets.length > 0 &&
                        <ThumbnailGridContainer>
                            <MultiSelectThumbnailGrid
                                items={filteredAssets}
                                columns={3}
                                colGap={30}
                                rowGap={30}
                                readOnly={!editable}
                                allowDragging={false}
                                allowMultiSelect={editable}
                                thumbnailClass={AssetThumbnail}
                                onItemSelected={selection => {
                                    if (addAssetCallback) {
                                        addAssetCallback(selection[0]);
                                        handleConfirm();
                                    }
                                }}
                                onDoubleClick={(asset: any) => handleEditSettings(asset)}
                                renderContextMenuItems={(selection: any) => renderContextMenu(selection)}
                            />
                        </ThumbnailGridContainer>
                    }
                    {filteredAssets.length === 0 && (
                        (
                            !searchTerm &&
                            selectedMediaFilter === "all" &&
                            selectedTagFilter === "  all  "
                        )
                            ? <NoTeamAssetNotice />
                            : <NoMatchNotice />
                    )}
                </StyledUIPaneResultsContainer>
            </UIPane>
        );
    }
);

export default TeamAssetsPane;

const ThumbnailBar = styled.div`
    display: flex;
    align-items: center;
    width: calc(100% + 10px);

    label {
        font-size: 14px;
        letter-spacing: 0px;
        color: #333;
        white-space: nowrap;
        text-overflow: ellipsis;
        overflow: hidden;
        text-align: left;
        flex-grow: 2;
        text-transform: none;
        margin: 0px;
    }
`;

interface AssetThumbnailProps {
    item: IWorkspaceResourceAsset;
    onMouseDown: (event: React.MouseEvent) => void;
    onDoubleClick: (item: any) => void;
    onContextMenu: (event: React.MouseEvent) => void;
    selected: boolean;
    readOnly: boolean;
    allSelected: IWorkspaceResourceAsset[];
    renderContextMenuItems: (selection: IWorkspaceResourceAsset[]) => React.ReactNode;
}

const AssetThumbnail = (props: AssetThumbnailProps) => {
    const { item: asset, onMouseDown, onDoubleClick, onContextMenu, selected, readOnly, allSelected, renderContextMenuItems } = props;

    const [thumbnailUrl, setThumbnailUrl] = useState<string | null>(null);
    const [visible, setVisible] = useState<boolean>(false);

    const ref = useRef<HTMLDivElement>(null);

    const getThumbnailUrl = async () => {
        const assetModel = await ds.assets.getAssetById(asset.resourceId);
        if (assetModel.get("actualId") !== asset.metadata.actualId) {
            await assetModel.reload();
        }
        return await assetModel.getURL(ASSET_RESOLUTION.XSMALL);
    };

    const fetchThumbnailUrl = () => {
        const currentThumbnailUrl = thumbnailUrl;

        setThumbnailUrl(null);
        getThumbnailUrl()
            .then(url => setThumbnailUrl(url))
            .catch(err => {
                logger.error(err, "[TeamAssetsPane] [AssetThumbnail] getThumbnailUrl() failed");
                if (currentThumbnailUrl) {
                    // Restore previous thumbnail if fetch fails
                    setThumbnailUrl(currentThumbnailUrl);
                }
            });
    };

    useEffect(() => {
        if (visible) {
            fetchThumbnailUrl();
        }
    }, [asset.metadata.actualId]);

    useEffect(() => {
        const observer = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting === true) {
                if (_.isEmpty(thumbnailUrl)) {
                    fetchThumbnailUrl();
                }
                if (!visible) {
                    setVisible(true);
                }
            } else {
                if (visible) {
                    setVisible(false);
                }
            }
        });

        if (ref.current) {
            observer.observe(ref.current);
        }

        return () => {
            observer.disconnect();
        };
    }, [thumbnailUrl, visible]);

    return (
        <ThumbnailContainer
            onMouseDown={onMouseDown}
            onDoubleClick={!readOnly ? onDoubleClick : undefined}
            onContextMenu={!readOnly ? onContextMenu : undefined}
            ref={ref}
        >
            <Thumbnail url={thumbnailUrl} selected={selected} />
            <ThumbnailBar>
                <label className="presentation_item_name">{asset.metadata.name}</label>
                {!asset.metadata.isPublished && (
                    <Icon>visibility_off</Icon>
                )}
                {!readOnly && (
                    <IconMenu>{renderContextMenuItems(allSelected)}</IconMenu>
                )}
            </ThumbnailBar>
        </ThumbnailContainer>
    );
};
